import { ChampsOrigine } from '../tracabilite/ChampsOrigine';
import { champsTraceContact } from '../../lib/tracabilite';
import { useState } from 'react';
import { X, UserPlus } from '../../lib/icons';
import { toast } from 'sonner';
import { createContact, TYPES_CONTRAT } from '../../lib/dataProvider';

const field =
  'mt-1.5 h-11 w-full border border-slate-300 bg-slate-50 px-3 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

const TYPES = [
  { id: 'tiers', label: 'Tiers (client / fournisseur)', hint: 'Compte courant unique : prestation + achats' },
  { id: 'personnel', label: 'Personnel', hint: 'Salarié, saisonnier (CIN / CNSS)' },
  { id: 'transporteur', label: 'Transporteur', hint: 'Logistique' },
  { id: 'industriel', label: 'Industriel', hint: 'Grignons, huile lampante' },
];

const TYPES_TIERS_CHOIX = [
  { id: 'fournisseur_olives', label: 'Fournisseur Olives' },
  { id: 'fournisseur_general', label: 'Fournisseur Général' },
  { id: 'client', label: 'Client' },
];

export const ContactQuickForm = ({ typeInitial = 'tiers', role, onClose, onCreated, onVerifier }) => {
  const [f, setF] = useState({ type: typeInitial, nom: '', tel: '', adresse: '', mf: '', cin: '', cnss: '', contrat: TYPES_CONTRAT[0], salaireJour: '', salaireMois: '', region: '', typeTiers: role === 'fournisseur' ? 'fournisseur_olives' : 'client', estClient: role !== 'fournisseur', estFournisseur: role === 'fournisseur' });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const bascule = (k) => () => setF((p) => ({ ...p, [k]: !p[k] }));

  const valider = async (e) => {
    e.preventDefault();
    if (f.type === 'tiers' && !f.estClient && !f.estFournisseur && f.typeTiers !== 'fournisseur_general')
      return toast.error('Cochez au moins un rôle : Client et/ou Fournisseur.');
    // Déduplication : rapprochement avec la base des tiers avant création.
    if (onVerifier && f.type === 'tiers' && onVerifier({ nom: f.nom, tel: f.tel, cin: f.cin })) return;
    const { error, contact, fusionne, message } = await createContact({ ...f, ...champsTraceContact(f), role });
    if (error) return toast.error(error);
    if (fusionne) toast.warning(message);
    else toast.success(`${contact.nom} ajouté aux contacts — code ${contact.code}`);
    onCreated(contact);
  };

  return (
    <div
      data-testid="contact-quick-overlay"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4"
    >
      <form
        onSubmit={valider}
        data-testid="contact-quick-form"
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto border border-slate-200 bg-white"
      >
        <div className="flex items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-slate-900">
              <UserPlus size={18} /> Nouveau contact
            </h2>
            <p className="font-data text-[11px] uppercase tracking-[0.1em] text-slate-500">
              Code contact attribué automatiquement (CT-0001, CT-0002…)
            </p>
          </div>
          <button
            type="button"
            data-testid="contact-quick-close"
            onClick={onClose}
            className="border border-slate-200 p-2 text-slate-500 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
          >
            <X size={16} />
          </button>
        </div>

        <div className="space-y-4 px-5 py-5">
          {f.type !== "personnel" && <ChampsOrigine value={f} onChange={setF} />}
          <div>
            <label className={label}>Catégorie de contact</label>
            <div className="mt-2 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2">
              {TYPES.map((t) => {
                const active = f.type === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`contact-type-${t.id}`}
                    onClick={() => setF((p) => ({ ...p, type: t.id }))}
                    className={`px-3 py-3 text-left transition-colors duration-150 ${
                      active ? 'bg-slate-900 text-white' : 'bg-white hover:bg-slate-50'
                    }`}
                  >
                    <p className="text-sm font-bold">{t.label}</p>
                    <p className={`text-xs ${active ? 'text-slate-300' : 'text-slate-500'}`}>{t.hint}</p>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className={label}>Nom / Raison sociale</label>
            <input data-testid="contact-nom" value={f.nom} onChange={set('nom')} className={field} autoFocus />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className={label}>Téléphone</label>
              <input data-testid="contact-tel" value={f.tel} onChange={set('tel')} className={field} />
            </div>
            <div>
              <label className={label}>Région</label>
              <input data-testid="contact-region" value={f.region} onChange={set('region')} className={field} />
            </div>
          </div>

          <div>
            <label className={label}>Adresse</label>
            <input data-testid="contact-adresse" value={f.adresse} onChange={set('adresse')} className={field} />
          </div>

          {f.type === 'personnel' ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={label}>N° CIN</label>
                  <input data-testid="contact-cin" value={f.cin} onChange={set('cin')} className={field} />
                </div>
                <div>
                  <label className={label}>N° CNSS</label>
                  <input data-testid="contact-cnss" value={f.cnss} onChange={set('cnss')} className={field} />
                </div>
              </div>
              <div>
                <label className={label}>Type de contrat</label>
                <select data-testid="contact-contrat" value={f.contrat} onChange={set('contrat')} className={field}>
                  {TYPES_CONTRAT.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={label}>Salaire journalier (Shift) — TND</label>
                  <input data-testid="contact-salaire-jour" type="number" step="0.001" min="0" value={f.salaireJour} onChange={set('salaireJour')} className={field} />
                </div>
                <div>
                  <label className={label}>Salaire mensuel — TND</label>
                  <input data-testid="contact-salaire-mois" type="number" step="0.001" min="0" value={f.salaireMois} onChange={set('salaireMois')} className={field} />
                </div>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className={label}>Rôles (une seule fiche, plusieurs rôles)</label>
                <div className="mt-2 flex flex-wrap gap-4">
                  {[['estClient', 'Client (prestation de trituration)'], ['estFournisseur', 'Fournisseur (vente d’olives)']].map(([k, lib]) => (
                    <label key={k} className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                      <input type="checkbox" data-testid={`contact-role-${k}`} checked={f[k]} onChange={bascule(k)} className="h-4 w-4 accent-slate-900" />
                      {lib}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <label className={label}>Type de tiers (segmentation comptable)</label>
                <select data-testid="contact-type-tiers" value={f.typeTiers} onChange={set('typeTiers')} className={field}>
                  {TYPES_TIERS_CHOIX.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
                <p className="mt-1.5 text-[11px] text-slate-500">
                  Seuls les « Fournisseurs Généraux » apparaissent dans le module Dépenses & Frais Généraux.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className={label}>Matricule fiscal (MF)</label>
                  <input data-testid="contact-mf" value={f.mf} onChange={set('mf')} className={field} />
                </div>
                <div>
                  <label className={label}>CIN</label>
                  <input data-testid="contact-cin" value={f.cin} onChange={set('cin')} className={field} />
                </div>
              </div>
              <p className="border-l-2 border-slate-900 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                Anti-doublon : si la CIN, le MF, le téléphone ou le nom existe déjà, la fiche existante est
                complétée et devient un profil mixte (client ET fournisseur).
              </p>
            </>
          )}
        </div>

        <div className="flex gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="submit"
            data-testid="contact-quick-submit"
            className="h-11 flex-1 bg-slate-900 font-heading text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700"
          >
            Créer et sélectionner
          </button>
          <button
            type="button"
            data-testid="contact-quick-cancel"
            onClick={onClose}
            className="h-11 border border-slate-300 px-5 text-sm font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-100"
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
};
